import * as Sentry from '@sentry/browser';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

let initialized = false;

export function initSentry() {
  if (initialized || !SENTRY_DSN || import.meta.env.DEV) return;
  initialized = true;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // 'development' | 'production'
    // Capture 100% of transactions in dev, 10% in production
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1,
    // Session replay: 10% of sessions, 100% on error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Supabase auth-js uses the Web Locks API to serialize token refreshes across
    // tabs. When a tab loses the race it throws a LockManager timeout — this is
    // expected behaviour (the winning tab will refresh the token) and is not
    // actionable from our side.
    ignoreErrors: [
      /Acquiring an exclusive Navigator LockManager lock .* timed out/,
    ],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
  });
}

/** Tag the current Sentry scope with the logged-in user. */
export function setSentryUser(id: string, email?: string) {
  if (!initialized) return;
  Sentry.setUser({ id, email });
}

/** Clear the user on sign-out. */
export function clearSentryUser() {
  if (!initialized) return;
  Sentry.setUser(null);
}

/** Manually report a caught error with optional context. */
export function captureError(error: unknown, context?: Record<string, unknown>) {
  if (!initialized) return;
  Sentry.withScope((scope) => {
    if (context) scope.setContext('extra', context);
    Sentry.captureException(error);
  });
}

